// src/pages/Database.jsx - With Horizontal Navigation
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import SearchNavigation from '../components/SearchNavigation';
import ApiService from '../services/apiService';

const Database = () => {
  const navigate = useNavigate();

  // State management
  const [courts, setCourts] = useState([]);
  const [selectedCourt, setSelectedCourt] = useState(null);
  const [isLoadingCourts, setIsLoadingCourts] = useState(true);
  const [courtsError, setCourtsError] = useState('');

  const [judgements, setJudgements] = useState([]);
  const [isLoadingJudgements, setIsLoadingJudgements] = useState(false);
  const [totalJudgements, setTotalJudgements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const [currentPage, setCurrentPage] = useState(1);
 const currentYear = new Date().getFullYear();
const [years] = useState(
  Array.from({ length: currentYear - 1950 + 1 }, (_, i) => (currentYear - i).toString())
);
const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());


  const resultsPerPage = 25;

  const [allowedCourtKeys, setAllowedCourtKeys] = useState([]);


  useEffect(() => {
    loadCourts();
  }, []);

const loadCourts = async () => {
  try {
    setIsLoadingCourts(true);
    setCourtsError('');

    const permissionsData = JSON.parse(localStorage.getItem('userp'));
    const allCourts = permissionsData.courts || [];

    // Filter to only SC and Bombay HC
    const allowedCourts = allCourts.filter(court =>
      court.value?.toLowerCase().includes('supreme') ||
      court.value?.toLowerCase().includes('bombay')
    );

    // Store their keys for use in "All Courts" query
    const allowedKeys = allowedCourts.map(c => c.key);
    setAllowedCourtKeys(allowedKeys);

    // Add "All Courts" option at the top
    const finalCourts = [{ key: 'All', value: 'All Courts' }, ...allowedCourts];
    setCourts(finalCourts);

    // Auto-select "All Courts"
    setSelectedCourt('All');
    loadJudgements('All', currentPage, selectedYear, allowedKeys);

  } catch (error) {
    console.error('❌ Error loading courts:', error);
    setCourtsError(error.message || 'Failed to load courts');
  } finally {
    setIsLoadingCourts(false);
  }
};

  const formatDate = (dateStr) => {
    if (dateStr) {
      try {
        if (dateStr.length === 8) {
          const year = dateStr.substring(0, 4);
          const month = dateStr.substring(4, 6);
          const day = dateStr.substring(6, 8);
          return `${day}/${month}/${year}`;
        }
        return dateStr;
      } catch {
        return dateStr;
      }
    }
    return 'Date not available';
  };

// ✅ Updated loadJudgements — accepts optional keysOverride for first load
const loadJudgements = async (courtKey, page, year, keysOverride) => {
  try {
    setIsLoadingJudgements(true);

    // When "All" is selected, use only SC & Bom HC keys (not empty array)
    const keys = keysOverride || allowedCourtKeys;
    const mainkeys = courtKey === 'All' ? keys : [courtKey];

    const payload = {
      requests: [{
        yearFrom: year,
        yearTo: year,
        mainkeys: mainkeys,   // ✅ Only SC & Bom HC when "All" is selected
      }],
      sortBy: "year",
      sortOrder: "desc",
      page: page || 1,
      pageSize: resultsPerPage,
      inst: '',
      prompt: "Database",
    };

    const apiResponse = await ApiService.executeSearchFilter(payload);
    setJudgements(apiResponse.hits);
    setTotalJudgements(apiResponse.total);

    if (page == '1')
      setTotalPages(Math.ceil(apiResponse.total / resultsPerPage));

    const resultsData = {
      results: apiResponse.hits || [],
      totalCount: apiResponse.total || 0,
      query: ``.trim(),
      searchType: 'Database Search',
      timestamp: new Date().toISOString(),
      courtsList: apiResponse.courtsList || [],
      yearList: apiResponse.yearList || [],
      searchData: {
        yearFrom: year,
        yearTo: year,
        mainkeys: mainkeys,
      }
    };

    localStorage.setItem('searchResults', JSON.stringify(resultsData));

  } catch (error) {
    console.error('❌ Error loading judgements:', error);
  } finally {
    setIsLoadingJudgements(false);
  }
};

  const handleCourtSelect = (courtKey) => {
    setSelectedCourt(courtKey);
    setCurrentPage(1);
    loadJudgements(courtKey, 1, selectedYear);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    loadJudgements(selectedCourt, page, selectedYear);
    window.scrollTo(0, 0);
  };

  const handleYearChange = (year) => {
    setSelectedYear(year);
    setCurrentPage(1);
    loadJudgements(selectedCourt, 1, year);
  };

  const handleJudgementClick = (judgement) => {
    const currentIndex = judgements.findIndex(result => result.id === judgement.id);
    const navigationData = {
      currentIndex: currentIndex,
      results: judgements.map(result => ({
        keycode: result.id,
        title: '',
        court: '',
        date: ''
      })),
      searchQuery: '',
      totalResults: totalJudgements,
      currentPage: currentPage
    };

    sessionStorage.setItem('judgementNavigation', JSON.stringify(navigationData));
    navigate(`/judgement/${judgement.id}`);
  };

  const startResult = (currentPage - 1) * resultsPerPage + 1;
  const endResult = Math.min(currentPage * resultsPerPage, totalJudgements);

  return (
    <div className="database-layout">
      <Navbar />
      <SearchNavigation />

      <div className="database-main-content">
        {/* Hero Section */}
        <div className="database-hero">
          <h1 className="hero-title">Legal Database</h1>
          <p className="hero-subtitle">
            Comprehensive access to judgements with powerful search and filtering
          </p>
        </div>

        <div className="content-wrapper">
          {/* Left Sidebar - Courts List */}
          <div className="courts-sidebar">
            <div className="courts-header">
              <i className="bx bx-building"></i>
              Select Court
            </div>

            {isLoadingCourts ? (
              <div className="loading-state">
                <i className="bx bx-loader-alt bx-spin"></i>
                <p>Loading courts...</p>
              </div>
            ) : courtsError ? (
              <div className="error-state">
                <i className="bx bx-error-circle"></i>
                <p>{courtsError}</p>
              </div>
            ) : (
              <div className="courts-list">
                {courts.map((court) => (
                  <div
                    key={court.key}
                    className={`court-item ${selectedCourt === court.key ? 'active' : ''}`}
                    onClick={() => handleCourtSelect(court.key)}
                  >
                    <i className="bx bx-chevron-right"></i>
                    <span>{court.value}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Right Content - Judgements Table */}
          <div className="judgements-content">
            {/* Top Controls */}
            <div className="top-controls">
              <div className="controls-left">
                <div className="page-selector">
                  <label>Page:</label>
                  <select
                    value={currentPage}
                    onChange={(e) => handlePageChange(Number(e.target.value))}
                    className="modern-select"
                  >
                    {[...Array(Math.min(totalPages))].map((_, i) => (
                      <option key={i + 1} value={i + 1}>{i + 1}</option>
                    ))}
                  </select>
                </div>

                <div className="results-info">
                  <i className="bx bx-file"></i>
                  Showing {startResult} to {endResult} of {totalJudgements.toLocaleString()}
                </div>
              </div>

              <div className="controls-right">
                <div className="total-badge">
                  Total: <strong>{totalJudgements.toLocaleString()}</strong>
                </div>

                <div className="year-selector">
                  <label>Year:</label>
                  <select
                    value={selectedYear}
                    onChange={(e) => handleYearChange(e.target.value)}
                    className="modern-select"
                  >
                    {years.map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Judgements Table */}
            {isLoadingJudgements ? (
              <div className="loading-table">
                <i className="bx bx-loader-alt bx-spin"></i>
                <p>Loading judgements...</p>
              </div>
            ) : (
              <div className="table-container">
                <table className="judgements-table">
                  <thead>
                    <tr>
                      <th className="col-icon"></th>
                      <th className="col-appellant">APPELLANT</th>
                      <th className="col-respondent">RESPONDENT</th>
                      <th className="col-date">DATE</th>
                      <th className="col-court">COURT</th>
                    </tr>
                  </thead>
                  <tbody>
                    {judgements.map((judgement) => (
                      <tr
                        key={judgement.id}
                        onClick={() => handleJudgementClick(judgement)}
                        className="judgement-row"
                      >
                        <td className="col-icon">
                          <i className="bx bx-right-arrow-circle"></i>
                        </td>
                        <td className="col-appellant">{judgement.appellant}</td>
                        <td className="col-respondent">{judgement.respondent}</td>
                        <td className="col-date">{formatDate(judgement.date)}</td>
                        <td className="col-court">{judgement.court}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        /* Layout */
        .database-layout {
          min-height: 100vh;
          background: #fafafa;
          padding-top: 140px; /* Navbar (74px) + SearchNav (66px) */
        }

        .database-main-content {
          width: 100%;
        }

        /* Hero Section */
        .database-hero {
          background: linear-gradient(135deg, #E91E63 0%, #E91E64 25%, #c41849 75%, #D81B60 100%);
          padding: 40px 20px;
          text-align: center;
          color: white;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
        }

        .hero-title {
          font-size: 2.5rem;
          font-weight: 800;
          margin: 0 0 10px 0;
          text-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
          letter-spacing: -0.5px;
          line-height: 1.2;
        }

        .hero-subtitle {
          font-size: 1.1rem;
          opacity: 0.95;
          margin: 0;
          font-weight: 400;
        }

        /* Content Wrapper */
        .content-wrapper {
          display: flex;
          gap: 0;
          min-height: calc(100vh - 300px);
          max-width: 100%;
          margin: 0 auto;
          background: white;
          box-shadow: 0 0 30px rgba(0, 0, 0, 0.08);
        }

        /* Courts Sidebar */
        .courts-sidebar {
          width: 280px;
          background: white;
          border-right: 1px solid #e0e0e0;
          overflow-y: auto;
          flex-shrink: 0;
        }

        .courts-header {
          padding: 18px 20px;
          background: linear-gradient(135deg, #E91E63 0%, #c41849 100%);
          color: white;
          font-weight: 700;
          font-size: 15px;
          position: sticky;
          top: 0;
          z-index: 10;
          display: flex;
          align-items: center;
          gap: 10px;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
        }

        .courts-header i {
          font-size: 20px;
        }

        .courts-list {
          padding: 10px 0;
        }

        .court-item {
          padding: 12px 20px;
          cursor: pointer;
          transition: all 0.2s;
          border-left: 3px solid transparent;
          font-size: 14px;
          color: #333;
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .court-item i {
          font-size: 16px;
          color: #999;
          transition: all 0.2s;
        }

        .court-item:hover {
          background: #fff5f0;
          border-left-color: #E91E63;
        }

        .court-item:hover i {
          color: #E91E63;
          transform: translateX(3px);
        }

        .court-item.active {
          background: linear-gradient(90deg, rgba(245, 166, 35, 0.1), rgba(233, 30, 99, 0.05));
          border-left-color: #c41849;
          color: #c41849;
          font-weight: 600;
        }

        .court-item.active i {
          color: #c41849;
        }

        .loading-state, .error-state {
          padding: 40px 20px;
          text-align: center;
          color: #666;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 10px;
        }

        .loading-state i, .error-state i {
          font-size: 32px;
          color: #E91E63;
        }

        /* Judgements Content */
        .judgements-content {
          flex: 1;
          display: flex;
          flex-direction: column;
          background: white;
          overflow: hidden;
        }

        .top-controls {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 15px;
          padding: 18px 20px;
          background: #fafafa;
          border-bottom: 2px solid #e0e0e0;
          flex-wrap: wrap;
        }

        .controls-left, .controls-right {
          display: flex;
          align-items: center;
          gap: 15px;
          flex-wrap: wrap;
        }

        .page-selector, .year-selector {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .page-selector label, .year-selector label {
          font-size: 14px;
          font-weight: 600;
          color: #555;
          white-space: nowrap;
        }

        .modern-select {
          padding: 8px 35px 8px 14px;
          border: 2px solid #e0e0e0;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 600;
          background: white;
          color: #333;
          cursor: pointer;
          appearance: none;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23F5A623' d='M6 9L1 4h10z'/%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 10px center;
          transition: all 0.2s;
        }

        .modern-select:hover {
          border-color: #E91E63;
        }

        .modern-select:focus {
          outline: none;
          border-color: #c41849;
          box-shadow: 0 0 0 3px rgba(233, 30, 99, 0.1);
        }

        .results-info {
          font-size: 13px;
          color: #666;
          display: flex;
          align-items: center;
          gap: 8px;
          white-space: nowrap;
        }

        .results-info i {
          color: #E91E63;
          font-size: 16px;
        }

        .total-badge {
          background: linear-gradient(135deg, #E91E63, #c41849);
          color: white;
          padding: 8px 16px;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 600;
          box-shadow: 0 2px 8px rgba(245, 166, 35, 0.3);
          white-space: nowrap;
        }

        .total-badge strong {
          font-weight: 800;
        }

        /* Table */
        .table-container {
          flex: 1;
          overflow: auto;
        }

        .judgements-table {
          width: 100%;
          border-collapse: collapse;
        }

        .judgements-table thead {
          position: sticky;
          top: 0;
          z-index: 10;
          background: linear-gradient(135deg, rgba(245, 166, 35, 0.1), rgba(233, 30, 99, 0.05));
        }

        .judgements-table th {
          padding: 14px 12px;
          text-align: left;
          font-weight: 700;
          font-size: 12px;
          color: #c41849;
          border-bottom: 2px solid #c41849;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .col-icon {
          width: 50px;
          text-align: center !important;
        }

        .col-appellant { width: 30%; }
        .col-respondent { width: 30%; }
        .col-date { width: 15%; }
        .col-court { width: 20%; }

        .judgement-row {
          cursor: pointer;
          transition: all 0.2s;
        }

        .judgement-row:hover {
          background: linear-gradient(90deg, rgba(245, 166, 35, 0.05), rgba(233, 30, 99, 0.05));
          transform: translateX(3px);
        }

        .judgement-row:nth-child(even) {
          background: #fafafa;
        }

        .judgement-row:nth-child(even):hover {
          background: linear-gradient(90deg, rgba(245, 166, 35, 0.08), rgba(233, 30, 99, 0.08));
        }

        .judgements-table td {
          padding: 12px;
          font-size: 13px;
          color: #333;
          border-bottom: 1px solid #f0f0f0;
        }

        .col-icon i {
          font-size: 20px;
          color: #E91E63;
          transition: all 0.2s;
        }

        .judgement-row:hover .col-icon i {
          color: #c41849;
          transform: scale(1.2);
        }

        .loading-table {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 400px;
          gap: 15px;
        }

        .loading-table i {
          font-size: 48px;
          color: #E91E63;
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .bx-spin {
          animation: spin 1s linear infinite;
        }

        /* Scrollbar */
        .courts-sidebar::-webkit-scrollbar,
        .table-container::-webkit-scrollbar {
          width: 8px;
        }

        .courts-sidebar::-webkit-scrollbar-thumb,
        .table-container::-webkit-scrollbar-thumb {
          background: linear-gradient(135deg, #E91E63, #c41849);
          border-radius: 4px;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .database-layout {
            padding-top: 130px;
          }

          .database-hero {
            padding: 30px 15px;
          }

          .hero-title {
            font-size: 1.8rem;
          }

          .content-wrapper {
            flex-direction: column;
          }

          .courts-sidebar {
            width: 100%;
            max-height: 300px;
            border-right: none;
            border-bottom: 1px solid #e0e0e0;
          }
        }
              /* ============================================
           REMOVE TOP WHITE SPACE
        ============================================ */
        body {
          margin: 0 !important;
          padding: 0 !important;
        }
      `}</style>
    </div>
  );
};

export default Database;